import { Minus, Plus } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Button } from "@tellescope/design-system";

interface QuantityStepperProps {
  value: number;
  max: number;
  onChange: (quantity: number) => void;
  /** The product's name, so each button's accessible label says which product it changes. */
  productTitle: string;
}

export const QuantityStepper = ({ value, max, onChange, productTitle }: QuantityStepperProps) => {
  const { t } = useTranslation("shop");

  return (
    <div
      className="inline-flex items-center gap-2"
      role="group"
      aria-label={t("Quantity of {{title}}", { title: productTitle })}
    >
      <Button
        data-test="quantity-decrease"
        variant="outline"
        size="xs"
        iconOnly
        hoverLabel={t("Decrease quantity")}
        aria-label={t("Decrease quantity of {{title}}", { title: productTitle })}
        disabled={value <= 1}
        onClick={() => onChange(value - 1)}
      >
        <Minus className="w-3 h-3" />
      </Button>
      <span
        data-test="quantity-value"
        className="min-w-5 text-center text-sm font-medium tabular-nums"
        aria-live="polite"
      >
        {value}
      </span>
      <Button
        data-test="quantity-increase"
        variant="outline"
        size="xs"
        iconOnly
        hoverLabel={t("Increase quantity")}
        aria-label={t("Increase quantity of {{title}}", { title: productTitle })}
        disabled={value >= max}
        onClick={() => onChange(value + 1)}
      >
        <Plus className="w-3 h-3" />
      </Button>
    </div>
  );
};
